import React, {useState} from "react";
import {Badge, Card, CardBody, Col, Row} from "reactstrap";

const BuyerLeadProfile = ({
    fullName,
    email,
    phone,
    dobValue,
    formatDob,
    tags,
    accountStatus,
    emailConsent,
    emailConsentDate,
    smsConsent,
    smsConsentDate,
    pushConsent,
    formatDate,
    addresses,
    formatAddress,
    getAddressBadges,
    getAddressTitle,
    getAddressSubtitle,
    isAddressArchived,
    getAddressDates,
    copiedAddressIndexes,
    isDefaultAddress,
    onEditAddress,
    onCopyAddress,
    onAddAddress,
    onSetDefaultAddress,
    onRestoreAddress,
    onDeleteAddress,
    membershipTier,
    membershipSince,
    isVipMember,
    vipValidityDate,
    nextTier,
    tierProgressPercent,
    tierProgressLabel,
    pointsBalanceLabel,
    pointsValueLabel,
    pointsEarnedDisplay,
    pointsRedeemedDisplay,
    referralsLabel,
    referralCode,

    availableRewards,
    preferredSize,
    preferredSizeConfidence,
    preferredSizeOrders,
    sizeDistribution,
    fitReturns,
    getMeasurementValue,
    sizingLastUpdated,
    fitPreference,
    bodyShape,
    aiRecommendedSize,
    aiRecommendedNote,
    aiRecommendedWhy,
}) => {
    const addressList = addresses || [];

    const rewards = availableRewards || [];
    const vipStatusLabel = isVipMember ? "Active" : "Not a VIP member";
    const vipValidityLabel = isVipMember
        ? (vipValidityDate ? formatDate(vipValidityDate) : "No expiry date")
        : "-";

    const VISIBLE_COUNT = 2;
    const [scrollIndex, setScrollIndex] = useState(0);
    const safeIndex = addressList.length <= VISIBLE_COUNT
        ? 0
        : Math.min(scrollIndex, addressList.length - VISIBLE_COUNT);
    const visibleAddresses = addressList.slice(safeIndex, safeIndex + VISIBLE_COUNT);
    const canPrev = safeIndex > 0;
    const canNext = safeIndex + VISIBLE_COUNT < addressList.length;

    return (
        <>
            <Row>
                <Col sm="12">
                    <Card
                        id="buyer-lead-section-customer-details"
                        className="mb-3 buyer-lead-customer-card buyer-lead-section"
                    >
                        <div className="buyer-lead-section-header">
                            <div className="buyer-lead-section-icon">
                                <i className="fa fa-user"></i>
                            </div>
                            <div>
                                <div className="buyer-lead-section-title">Customer Details</div>
                                <div className="buyer-lead-section-subtitle">
                                    Identity, contact information and account status
                                </div>
                            </div>
                        </div>
                        <CardBody>
                            <Row className="buyer-lead-customer-fields">
                                <Col lg="6">
                                    <div className="buyer-lead-field">
                                        <div className="buyer-lead-field-label">Full Name</div>
                                        <div className="buyer-lead-field-value">{fullName}</div>
                                    </div>
                                    <div className="buyer-lead-field">
                                        <div className="buyer-lead-field-label">Email Address</div>
                                        <div className="buyer-lead-field-value">{email}</div>
                                    </div>
                                </Col>
                                <Col lg="6">
                                    <div className="buyer-lead-field">
                                        <div className="buyer-lead-field-label">Phone Number</div>
                                        <div className="buyer-lead-field-value">{phone}</div>
                                    </div>
                                    <div className="buyer-lead-field">
                                        <div className="buyer-lead-field-label">Date of Birth</div>
                                        <div className="buyer-lead-field-value">{formatDob(dobValue)}</div>
                                    </div>
                                </Col>
                            </Row>
                            <Row>
                                <Col lg="12">
                                    <div className="buyer-lead-field">
                                        <div className="buyer-lead-field-label">Customer Tags</div>
                                        <div className="buyer-lead-tags">
                                            {tags.length === 0 ? (
                                                <Badge color="light" className="mr-2 mb-2">
                                                    No tags
                                                </Badge>
                                            ) : (
                                                tags.map((tag, index) => (
                                                    <Badge
                                                        key={`${tag}-${index}`}
                                                        color="light"
                                                        className="mr-2 mb-2"
                                                    >
                                                        {tag}
                                                    </Badge>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                            <div className="buyer-lead-gdpr">
                                <div className="buyer-lead-gdpr-title">
                                    <i className="fa fa-shield mr-2"></i> GDPR & Data Protection
                                </div>
                                <div className="buyer-lead-gdpr-text">
                                    This customer has full data protection rights. Sensitive fields are masked by
                                    default and require appropriate permissions to view.
                                </div>
                                <div className="buyer-lead-gdpr-actions">
                                    <button type="button" className="btn btn-outline-primary btn-sm mr-2">
                                        Request Data Export
                                    </button>
                                    <button type="button" className="btn btn-outline-primary btn-sm">
                                        View Consent History
                                    </button>
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <Row>
                <Col sm="12">
                    <Card
                        id="buyer-lead-section-addresses"
                        className="buyer-lead-addresses-card buyer-lead-section"
                    >
                        <div className="buyer-lead-addresses-header">
                            <div className="buyer-lead-addresses-title">
                                <div className="buyer-lead-addresses-icon">
                                    <i className="fa fa-map-marker"></i>
                                </div>
                                <div>
                                    <div className="buyer-lead-addresses-heading">Addresses</div>
                                    <div className="buyer-lead-addresses-subtitle">
                                        Shipping and billing addresses with usage history
                                    </div>
                                </div>
                            </div>
                            <div className="buyer-lead-addresses-nav">
                                {addressList.length > VISIBLE_COUNT && (
                                    <>
                                        <button
                                            type="button"
                                            className="buyer-lead-addresses-nav-btn"
                                            disabled={!canPrev}
                                            onClick={() => setScrollIndex((i) => Math.max(0, i - 1))}
                                        >
                                            <i className="fa fa-chevron-left"></i>
                                        </button>
                                        <span className="buyer-lead-addresses-nav-count">
                                            {safeIndex + 1}–{Math.min(safeIndex + VISIBLE_COUNT, addressList.length)} of {addressList.length}
                                        </span>
                                        <button
                                            type="button"
                                            className="buyer-lead-addresses-nav-btn"
                                            disabled={!canNext}
                                            onClick={() => setScrollIndex((i) => Math.min(addressList.length - VISIBLE_COUNT, i + 1))}
                                        >
                                            <i className="fa fa-chevron-right"></i>
                                        </button>
                                    </>
                                )}
                                <button
                                    type="button"
                                    className="btn btn-success btn-sm"
                                    onClick={() => onAddAddress?.()}
                                >
                                    <i className="fa fa-plus mr-1"></i> Add Address
                                </button>
                            </div>
                        </div>
                        <CardBody>
                            <Row>
                                {addressList.length === 0 ? (
                                    <Col sm="12">
                                        <div className="text-muted">No addresses available.</div>
                                    </Col>
                                ) : (
                                    visibleAddresses.map((address, visibleIndex) => {
                                        const index = safeIndex + visibleIndex;
                                        const name = [address?.first_name, address?.last_name]
                                            .filter(Boolean)
                                            .join(" ");
                                        const company = address?.company;
                                        const addressLines = formatAddress(address);
                                        const badges = getAddressBadges(address);
                                        const {added, lastUsed} = getAddressDates(address);
                                        const isCopied =
                                            Array.isArray(copiedAddressIndexes) &&
                                            copiedAddressIndexes.includes(index);
                                        const isDefault = typeof isDefaultAddress === "function"
                                            ? isDefaultAddress(address)
                                            : address?.is_default === 1 || address?.is_default === "1";
                                        const isArchived = isAddressArchived(address);
                                        return (
                                            <Col
                                                lg="6"
                                                sm="12"
                                                key={`address-${index}`}
                                                className="buyer-lead-address-col"
                                            >
                                                <Card
                                                    className={`buyer-lead-address-card ${
                                                        isDefault ? "is-default" : ""
                                                    } ${isCopied ? "is-copied" : ""} ${
                                                        isArchived ? "is-archived" : ""
                                                    }`}
                                                >
                                                    <CardBody>
                                                        <div className="buyer-lead-address-card-header">
                                                            <div>
                                                                <div className="buyer-lead-address-title">
                                                                    {getAddressTitle(address)}
                                                                </div>
                                                                <div className="buyer-lead-address-subtitle">
                                                                    {getAddressSubtitle(address)}
                                                                </div>
                                                            </div>
                                                            <div className="buyer-lead-address-badges">
                                                                {badges.map((badge, badgeIndex) => (
                                                                    <Badge
                                                                        key={`${badge.label}-${badgeIndex}`}
                                                                        color={badge.color}
                                                                        pill
                                                                        className="mr-2 mb-1"
                                                                    >
                                                                        {badge.label}
                                                                    </Badge>
                                                                ))}
                                                            </div>
                                                        </div>
                                                        <div className="buyer-lead-address-lines">
                                                            {name && <div>{name}</div>}
                                                            {company && <div>{company}</div>}
                                                            {addressLines.map((line, lineIndex) => (
                                                                <div key={`address-${index}-line-${lineIndex}`}>
                                                                    {line}
                                                                </div>
                                                            ))}
                                                        </div>
                                                        <div className="buyer-lead-address-meta">
                                                            <i className="fa fa-calendar mr-2"></i>
                                                            <span>Added: {formatDate(added)}</span>
                                                            <span className="mx-2">•</span>
                                                            <span>Last used: {formatDate(lastUsed)}</span>
                                                        </div>
                                                        <div className="buyer-lead-address-actions">
                                                            {isArchived ? (
                                                                <>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-outline-secondary btn-sm"
                                                                        onClick={() => onRestoreAddress?.(address, index)}
                                                                    >
                                                                        <i className="fa fa-undo mr-1"></i>
                                                                        Restore
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-outline-danger btn-sm"
                                                                        onClick={() => onDeleteAddress?.(address, index)}
                                                                    >
                                                                        <i className="fa fa-trash mr-1"></i>
                                                                        Delete
                                                                    </button>
                                                                </>
                                                            ) : isDefault ? (
                                                                <>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-outline-secondary btn-sm"
                                                                        onClick={() => onEditAddress?.(address, index)}
                                                                    >
                                                                        <i className="fa fa-pencil mr-1"></i>
                                                                        Edit
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-outline-secondary btn-sm"
                                                                        onClick={() => onCopyAddress?.(address, index)}
                                                                    >
                                                                        <i className="fa fa-copy mr-1"></i>
                                                                        Copy
                                                                    </button>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-outline-secondary btn-sm"
                                                                        onClick={() => onSetDefaultAddress?.(address, index)}
                                                                    >
                                                                        <i className="fa fa-star mr-1"></i>
                                                                        Set Default
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-outline-secondary btn-sm"
                                                                        onClick={() => onEditAddress?.(address, index)}
                                                                    >
                                                                        <i className="fa fa-pencil mr-1"></i>
                                                                        Edit
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-outline-danger btn-sm"
                                                                        onClick={() => onDeleteAddress?.(address, index)}
                                                                    >
                                                                        <i className="fa fa-trash mr-1"></i>
                                                                        Delete
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </CardBody>
                                                </Card>
                                            </Col>
                                        );
                                    })
                                )}
                            </Row>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <Row>
                <Col sm="12">
                    <Card
                        id="buyer-lead-section-membership"
                        className="buyer-lead-membership-card buyer-lead-section"
                    >
                        <div className="buyer-lead-membership-header">
                            <div>
                                <div className="buyer-lead-membership-title">
                                    <i className="fa fa-crown mr-2"></i> Membership
                                </div>
                                <div className="buyer-lead-membership-subtitle">
                                    Member details
                                </div>
                            </div>
                        </div>
                        <CardBody>
                            <Row className="mb-4">
                                <Col lg="4" md="6" className="mb-3">
                                    <div className="buyer-lead-tier-card">
                                        <div className="buyer-lead-tier-header">
                                            <div>
                                                <div className="buyer-lead-tier-name">{membershipTier}</div>
                                                <div className="buyer-lead-tier-sub">
                                                    Member since {formatDate(membershipSince)}
                                                </div>
                                            </div>
                                            <Badge color="light" pill className="buyer-lead-tier-badge">
                                                Current Tier
                                            </Badge>
                                        </div>
                                        <div className="buyer-lead-tier-next">Next tier: {nextTier}</div>
                                        <div className="buyer-lead-tier-progress">
                                            <div
                                                className="buyer-lead-tier-progress-bar"
                                                style={{width: `${tierProgressPercent}%`}}
                                            ></div>
                                        </div>
                                        <div className="buyer-lead-tier-progress-text">{tierProgressLabel}</div>
                                    </div>
                                </Col>
                            </Row>

                            <Row className="mb-4">
                                <Col lg="4" md="6" className="mb-3">
                                    <div className="buyer-lead-stat-card">
                                        <div className="buyer-lead-stat-label">VIP Membership</div>
                                        <div className="buyer-lead-stat-value">{vipStatusLabel}</div>
                                    </div>
                                </Col>
                                <Col lg="4" md="6" className="mb-3">
                                    <div className="buyer-lead-stat-card">
                                        <div className="buyer-lead-stat-label">VIP Validity</div>
                                        <div className="buyer-lead-stat-value">{vipValidityLabel}</div>
                                    </div>
                                </Col>
                            </Row>

                            <div className="buyer-lead-table-title mt-4">Available Rewards</div>
                            <Row>
                                {rewards.length === 0 ? (
                                    <Col sm="12">
                                        <div className="text-muted">No rewards available.</div>
                                    </Col>
                                ) : (
                                    rewards.map((reward, index) => (
                                        <Col lg="4" md="6" className="mb-3" key={`reward-${index}`}>
                                            <div className={`buyer-lead-reward-card ${reward.color || ""}`}>
                                                <div className="buyer-lead-reward-points">
                                                    {reward.points || reward.point || "-"} Points
                                                </div>
                                                <div className="buyer-lead-reward-title">
                                                    {reward.title || reward.name || "-"}
                                                </div>
                                                <div className="buyer-lead-reward-sub">
                                                    {reward.description || reward.details || "-"}
                                                </div>
                                                <button type="button" className="btn btn-primary btn-sm">
                                                    Redeem
                                                </button>
                                            </div>
                                        </Col>
                                    ))
                                )}
                            </Row>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <Row>
                <Col sm="12">
                    <Card
                        id="buyer-lead-section-sizing"
                        className="buyer-lead-sizing-card buyer-lead-section"
                    >
                        <div className="buyer-lead-sizing-header">
                            <div>
                                <div className="buyer-lead-sizing-title">
                                    <i className="fa fa-ruler-combined mr-2"></i> Sizing & Fit Profile
                                </div>
                                <div className="buyer-lead-sizing-subtitle">
                                    Size preferences, measurements, fit data and recommendations
                                </div>
                            </div>
                        </div>
                        <CardBody>
                            <Row className="mb-4">
                                <Col lg="6" className="mb-3">
                                    <div className="buyer-lead-size-card">
                                        <div className="buyer-lead-size-header">
                                            <div className="buyer-lead-size-title">Preferred Size</div>
                                            {preferredSizeConfidence !== null && (
                                                <Badge color="light" pill>
                                                    {preferredSizeConfidence}% confidence
                                                </Badge>
                                            )}
                                        </div>
                                        <div className="buyer-lead-size-value">{preferredSize}</div>
                                        <div className="buyer-lead-size-sub">
                                            {preferredSizeOrders !== null
                                                ? `Based on ${preferredSizeOrders} orders`
                                                : "Based on order history"}
                                        </div>
                                    </div>
                                    <div className="buyer-lead-size-distribution">
                                        <div className="buyer-lead-size-distribution-title">Size Distribution</div>
                                        {sizeDistribution.length === 0 ? (
                                            <div className="text-muted">No size distribution available.</div>
                                        ) : (
                                            sizeDistribution.map((item, index) => {
                                                const orders = item.orders ?? item.count ?? 0;
                                                const total = sizeDistribution.reduce(
                                                    (sum, entry) => sum + (entry.orders ?? entry.count ?? 0),
                                                    0
                                                );
                                                const percent = total > 0 ? Math.round((orders / total) * 100) : 0;
                                                return (
                                                    <div className="buyer-lead-size-bar" key={`size-${index}`}>
                                                        <div className="buyer-lead-size-bar-label">
                                                            <span>{item.size || item.label || "-"}</span>
                                                            <span>
                                                                {orders} orders ({percent}%)
                                                            </span>
                                                        </div>
                                                        <div className="buyer-lead-size-bar-track">
                                                            <div
                                                                className="buyer-lead-size-bar-fill"
                                                                style={{width: `${percent}%`}}
                                                            ></div>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                    <div className="buyer-lead-fit-returns">
                                        <div className="buyer-lead-size-distribution-title">Fit-Related Returns</div>
                                        {fitReturns.length === 0 ? (
                                            <div className="text-muted">No fit-related returns data.</div>
                                        ) : (
                                            fitReturns.map((item, index) => (
                                                <div className="buyer-lead-fit-return-item" key={`fit-${index}`}>
                                                    <span>{item.label || item.reason || "-"}</span>
                                                    <span>{item.count ?? item.items ?? "-"}</span>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </Col>
                                <Col lg="6" className="mb-3">
                                    <div className="buyer-lead-measurements-card disabled">
                                        <div className="buyer-lead-measurements-header">
                                            <div>Body Measurements</div>
                                            <Badge color="secondary" pill>Disabled</Badge>
                                        </div>
                                        <Row className="mt-3">
                                            {[
                                                {key: "bust", label: "Bust"},
                                                {key: "waist", label: "Waist"},
                                                {key: "hips", label: "Hips"},
                                                {key: "height", label: "Height"},
                                            ].map((item) => {
                                                const measurement = getMeasurementValue(item.key);
                                                return (
                                                    <Col sm="6" className="mb-3" key={item.key}>
                                                        <div className="buyer-lead-measurement">
                                                            <div className="buyer-lead-measurement-label">
                                                                {item.label}
                                                            </div>
                                                            <div className="buyer-lead-measurement-value">
                                                                {measurement.inchLabel}
                                                            </div>
                                                            <div className="buyer-lead-measurement-sub">
                                                                {measurement.cmLabel}
                                                            </div>
                                                        </div>
                                                    </Col>
                                                );
                                            })}
                                        </Row>
                                        <div className="buyer-lead-measurement-updated">
                                            Last updated: {formatDate(sizingLastUpdated)}
                                        </div>
                                    </div>

                                    <div className="buyer-lead-fit-preferences">
                                        <div className="buyer-lead-size-distribution-title">Fit Preferences</div>
                                        <div className="buyer-lead-fit-options">
                                            {["Tight", "Regular", "Loose"].map((option) => (
                                                <div
                                                    key={option}
                                                    className={`buyer-lead-fit-option ${
                                                        option.toLowerCase() === String(fitPreference).toLowerCase()
                                                            ? "is-active"
                                                            : ""
                                                    }`}
                                                >
                                                    {option}
                                                </div>
                                            ))}
                                        </div>
                                        <div className="buyer-lead-body-shape">
                                            <div className="buyer-lead-body-shape-label">Body Shape</div>
                                            <div className="buyer-lead-select-like">
                                                <span>{bodyShape}</span>
                                                <i className="fa fa-chevron-down"></i>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="buyer-lead-ai-card">
                                        <div className="buyer-lead-ai-title">
                                            <i className="fa fa-circle-info mr-2"></i> AI Size Recommendation
                                        </div>
                                        <div className="buyer-lead-ai-sub">
                                            Based on purchase history, measurements, and fit preferences, we recommend:
                                        </div>
                                        <div className="buyer-lead-ai-size">{aiRecommendedSize}</div>
                                        <div className="buyer-lead-ai-note">{aiRecommendedNote}</div>
                                        <div className="buyer-lead-ai-why">
                                            <strong>Why?</strong> {aiRecommendedWhy}
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </>
    );
};

export default BuyerLeadProfile;
